import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Mail, Phone, MapPin } from 'lucide-react';

const footerLinks = {
  Programmes: [
    { label: 'Property Qualifications', href: '/course-marketplace' },
    { label: 'Executive Education', href: '/course-marketplace' },
    { label: 'Cities & Infrastructure', href: '/course-marketplace' },
    { label: 'Industry Certificates', href: '/course-marketplace' },
    { label: 'Property Finance', href: '/course-marketplace' },
    { label: 'View All Programmes', href: '/course-marketplace' },
  ],
  University: [
    { label: 'About REUSA', href: '/#why-reusa' },
    { label: 'Advisory Council', href: '/advisory-council' },
    { label: 'Corporate Training', href: '/corporate-training' },
    { label: 'Accreditation', href: '/#accreditation' },
    { label: 'Founder\'s Letter', href: '/founders-letter' },
    { label: 'Admissions', href: '/apply' },
  ],
  'Student Life': [
    { label: 'Student Portal', href: '/login' },
    { label: 'Webinars & Events', href: '/#events' },
    { label: 'Community', href: '/#community' },
    { label: 'Career Services', href: '/#careers' },
    { label: 'Alumni Network', href: '/#alumni' },
    { label: 'Apply Now', href: '/apply' },
  ],
};

const institutionalLinks = [
  { label: 'Governance', href: '/governance' },
  { label: 'Advisory Council', href: '/advisory-council' },
  { label: "Founder\'s Letter", href: '/founders-letter' },
  { label: 'Accreditation Status', href: '/governance#accreditation' },
  { label: 'Contact Us', href: '/#contact' },
  { label: 'Admissions', href: '/apply' },
];

const socialLinks = [
  {
    label: 'LinkedIn',
    svg: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    ),
  },
  {
    label: 'Twitter/X',
    svg: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    svg: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
  },
  {
    label: 'YouTube',
    svg: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
        <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
      </svg>
    ),
  },
];

export default function LandingFooter() {
  return (
    <footer className="bg-primary text-white" id="contact">
      {/* Main footer */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="relative w-12 h-12 flex-shrink-0">
                <Image
                  src="/assets/images/REUSA_PNG_LOGO_2_-1779294693104.png"
                  alt="REUSA Official Crest"
                  fill
                  className="object-contain"
                  sizes="48px"
                />
              </div>
              <div>
                <span className="font-serif font-bold text-xl text-white block">REUSA</span>
                <span className="font-sans text-[0.6rem] text-white/40 tracking-widest uppercase">
                  Real Estate University of South Africa
                </span>
              </div>
            </div>
            <p className="font-sans text-sm text-white/55 leading-relaxed mb-6 max-w-xs">
              Africa&apos;s premier institution for real estate education. Shaping the next
              generation of developers, investors, and property leaders.
            </p>

            {/* Contact */}
            <div className="space-y-3 mb-6">
              {[
                { icon: Mail, text: 'admissions@reusa.education' },
                { icon: Phone, text: '+27 11 101 3600' },
                { icon: MapPin, text: 'Online' },
              ]?.map((c) => (
                <div key={`contact-${c?.text}`} className="flex items-start gap-3">
                  <c.icon size={14} className="text-accent mt-0.5 flex-shrink-0" />
                  <span className="font-sans text-xs text-white/55">{c?.text}</span>
                </div>
              ))}
            </div>

            {/* Social */}
            <div className="flex gap-2">
              {socialLinks?.map((s) => (
                <button
                  key={`social-${s?.label}`}
                  className="w-9 h-9 rounded-lg bg-white/8 border border-white/10 flex items-center justify-center text-white/50 hover:bg-accent hover:text-primary hover:border-accent transition-all duration-200"
                  aria-label={s?.label}
                >
                  {s?.svg}
                </button>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks)?.map(([category, links]) => (
            <div key={`footer-col-${category}`}>
              <h4 className="font-sans text-xs font-semibold tracking-widest uppercase text-white/30 mb-4">
                {category}
              </h4>
              <ul className="space-y-2.5">
                {links?.map((link) => (
                  <li key={`footer-link-${link?.label}`}>
                    <Link
                      href={link?.href}
                      className="font-sans text-sm text-white/55 hover:text-accent transition-colors duration-200"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* Institutional Information section */}
      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-8">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="flex-shrink-0">
              <h5 className="font-sans text-[0.65rem] font-semibold tracking-widest uppercase text-white/30 mb-4">
                Institutional Information
              </h5>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {institutionalLinks?.map((link) => (
                  <Link
                    key={`inst-${link?.label}`}
                    href={link?.href}
                    className="font-sans text-xs text-white/45 hover:text-accent transition-colors duration-200"
                  >
                    {link?.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-sans text-xs text-white/30">
            © 2024 REUSA — Real Estate University of South Africa. All rights reserved.
          </p>
          <div className="flex gap-5">
            {['Privacy Policy', 'Terms of Service', 'POPIA Compliance', 'Cookie Policy']?.map((item) => (
              <Link
                key={`legal-${item}`}
                href="#"
                className="font-sans text-xs text-white/30 hover:text-white/60 transition-colors"
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}